// The artist-facing "Medium" control and the physical parameters behind it.
//
// Watercolour, gouache, acrylic and oil differ in chemistry (gum arabic vs
// acrylic emulsion vs drying oil) and drying mechanism (evaporation vs film
// coalescence vs oxidation), so no single physical variable spans them. The
// Medium value instead interpolates between calibrated keyframes of the
// engine's physical parameters. Each parameter is interpolated in the space
// where it varies naturally (log space for rates and viscosities).
//
// Physical parameters (engine units; one length unit = one simulation cell):
//   water        water carried per unit of brush load (surface water depth)
//   body         paint body (pigment + binder vehicle) per unit of load (paint volume)
//   pigment      pigment amount per unit of load
//   binderGrip   how strongly binder holds pigment out of the water (0 = all pigment suspends in water)
//   mobility     thin-film mobility of the wet paint body (inverse viscosity)
//   yieldStress  Bingham yield threshold: slope the wet body can hold without flowing
//   bodyDrying   rate the paint body loses openness (per simulated second)
//   evaporation  multiplier on water evaporation
//   rewet        how far dried pigment re-dissolves when wetted (gum arabic ~1, acrylic 0)
//   pickup       fraction of wet surface paint a passing bristle can take up
//   heightGain   how much paint volume becomes visible relief
//   wetGloss     specular smoothness while wet (0..1); dryGloss once dry

const KEYS = ['water', 'body', 'pigment', 'binderGrip', 'mobility', 'yieldStress', 'bodyDrying', 'evaporation', 'rewet', 'pickup', 'heightGain', 'wetGloss', 'dryGloss'];
const LOG_KEYS = new Set(['mobility', 'bodyDrying', 'evaporation', 'yieldStress']);

export const MEDIUM_STOPS = Object.freeze([
  { id: 'water', name: 'Pure water', at: 0.0, water: 1.0, body: 0.0, pigment: 0.0, binderGrip: 0.0, mobility: 1, yieldStress: 0.001, bodyDrying: 0.05, evaporation: 1.0, rewet: 1.0, pickup: 0.5, heightGain: 0.0, wetGloss: 0.85, dryGloss: 0.0 },
  { id: 'watercolour_transparent', name: 'Transparent watercolour', at: 0.1, water: 0.9, body: 0.0, pigment: 0.08, binderGrip: 0.02, mobility: 1, yieldStress: 0.001, bodyDrying: 0.05, evaporation: 1.0, rewet: 0.9, pickup: 0.5, heightGain: 0.0, wetGloss: 0.8, dryGloss: 0.02 },
  { id: 'watercolour', name: 'Pigmented watercolour', at: 0.2, water: 0.75, body: 0.0, pigment: 0.22, binderGrip: 0.05, mobility: 1, yieldStress: 0.001, bodyDrying: 0.05, evaporation: 1.0, rewet: 0.8, pickup: 0.5, heightGain: 0.0, wetGloss: 0.75, dryGloss: 0.03 },
  { id: 'watercolour_heavy', name: 'Heavy watercolour', at: 0.3, water: 0.5, body: 0.04, pigment: 0.45, binderGrip: 0.15, mobility: 0.5, yieldStress: 0.003, bodyDrying: 0.08, evaporation: 1.0, rewet: 0.75, pickup: 0.55, heightGain: 0.05, wetGloss: 0.7, dryGloss: 0.04 },
  { id: 'gouache', name: 'Gouache', at: 0.42, water: 0.3, body: 0.25, pigment: 0.7, binderGrip: 0.45, mobility: 0.12, yieldStress: 0.01, bodyDrying: 0.12, evaporation: 1.0, rewet: 0.6, pickup: 0.6, heightGain: 0.15, wetGloss: 0.55, dryGloss: 0.02 },
  { id: 'acrylic_fluid', name: 'Fluid acrylic', at: 0.52, water: 0.15, body: 0.45, pigment: 0.6, binderGrip: 0.8, mobility: 0.15, yieldStress: 0.006, bodyDrying: 0.1, evaporation: 1.1, rewet: 0.0, pickup: 0.55, heightGain: 0.25, wetGloss: 0.6, dryGloss: 0.35 },
  { id: 'acrylic_heavy', name: 'Heavy-body acrylic', at: 0.62, water: 0.05, body: 0.8, pigment: 0.75, binderGrip: 0.95, mobility: 0.03, yieldStress: 0.05, bodyDrying: 0.07, evaporation: 1.1, rewet: 0.0, pickup: 0.5, heightGain: 0.6, wetGloss: 0.55, dryGloss: 0.3 },
  { id: 'oil_thin', name: 'Thin oil (with medium)', at: 0.72, water: 0.0, body: 0.6, pigment: 0.45, binderGrip: 1.0, mobility: 0.08, yieldStress: 0.015, bodyDrying: 0.006, evaporation: 1.0, rewet: 0.0, pickup: 0.7, heightGain: 0.45, wetGloss: 0.8, dryGloss: 0.5 },
  { id: 'oil', name: 'Traditional oil', at: 0.84, water: 0.0, body: 1.0, pigment: 0.75, binderGrip: 1.0, mobility: 0.02, yieldStress: 0.08, bodyDrying: 0.004, evaporation: 1.0, rewet: 0.0, pickup: 0.65, heightGain: 0.85, wetGloss: 0.8, dryGloss: 0.55 },
  { id: 'oil_impasto', name: 'Heavy impasto oil', at: 1.0, water: 0.0, body: 1.6, pigment: 0.85, binderGrip: 1.0, mobility: 0.006, yieldStress: 0.25, bodyDrying: 0.003, evaporation: 1.0, rewet: 0.0, pickup: 0.55, heightGain: 1.0, wetGloss: 0.75, dryGloss: 0.5 },
]);

function interpolate(a, b, t, key) {
  if (LOG_KEYS.has(key)) {
    const la = Math.log(Math.max(1e-6, a));
    const lb = Math.log(Math.max(1e-6, b));
    return Math.exp(la + (lb - la) * t);
  }
  return a + (b - a) * t;
}

export function mediumAt(value) {
  const m = Math.max(0, Math.min(1, Number(value) || 0));
  let upper = MEDIUM_STOPS.findIndex((stop) => stop.at >= m);
  if (upper <= 0) upper = Math.max(1, upper);
  const a = MEDIUM_STOPS[upper - 1];
  const b = MEDIUM_STOPS[upper];
  const t = Math.max(0, Math.min(1, (m - a.at) / (b.at - a.at)));
  const params = { value: m, nearest: (t < 0.5 ? a : b).id, nearestName: (t < 0.5 ? a : b).name };
  for (const key of KEYS) params[key] = interpolate(a[key], b[key], t, key);
  return Object.freeze(params);
}

export function mediumStop(id) {
  return MEDIUM_STOPS.find((stop) => stop.id === id) || null;
}

// Applies the detailed (advanced) overrides on top of the Medium interpolation.
// Overrides are multipliers so they stay meaningful as the Medium value moves.
export function resolveMedium(value, overrides = {}) {
  const base = mediumAt(value);
  const out = { ...base };
  const mul = (key, factor) => {
    if (Number.isFinite(factor)) out[key] = base[key] * factor;
  };
  mul('water', overrides.waterScale);
  mul('pigment', overrides.pigmentScale);
  mul('body', overrides.thicknessScale);
  mul('mobility', overrides.fluidityScale);
  mul('bodyDrying', overrides.dryingScale);
  mul('evaporation', overrides.dryingScale);
  mul('pickup', overrides.pickupScale);
  return Object.freeze(out);
}
